import { Box, Container, Typography } from "@mui/material";
import clsx from "clsx";
import { TFunction } from "i18next";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ENV_VARS } from "../../../../config/env.ts";
import LinkConstants from "../../../../constants/LinkConstants.ts";
import { POSTHOG_EVENTS } from "../../../../constants/PostHogEvents";
import RouterConstants from "../../../../constants/RouterConstants.ts";
import { ORG_SUBSCRIPTION_PLANS } from "../../../../constants/billing.ts";
import { useApiLimitStatus } from "../../../../hooks/useApiLimitStatus.tsx";
import { usePostHog } from "../../../../hooks/usePostHog.tsx";
import useSecureNavigate from "../../../../hooks/useSecureNavigate.tsx";
import { useAppStore } from "../../../../hooks/useStores.tsx";
import { OrgSubscriptionPlan } from "../../../../models/billing.ts";
import BriaButton from "../../../common/BriaButton/BriaButton.tsx";
import BriaFaqSection from "../../../common/BriaFaqSection/BriaFaqSection.tsx";
import { FaqItem } from "../../../common/BriaFaqSection/faq.ts";
import BriaTooltip from "../../../common/BriaTooltip/BriaTooltip.tsx";
import PricingCard from "../PricingCard/PricingCard.tsx";
import { usePricingFeatureContent } from "../hooks/usePricingFeatureContent.ts";
import styles from "./PricingContent.module.scss";

interface Features {
	[key: string]: {
		title: string;
		tooltip: string;
		starter: string;
		pro: string;
		enterprise: string;
		tooltipLink?: string;
		showActionsPricingTooltip?: boolean;
	};
}

interface IPricingContentProps {
	onPlanClick?: (plan: OrgSubscriptionPlan) => void;
	isPublicPage?: boolean;
	onClose?: () => void;
	className?: string;
}

function getPricingFaqs(t: TFunction, isLegacy14DayTrialCohort: boolean): FaqItem[] {
	return [
		{
			question: isLegacy14DayTrialCohort ? t("faq.questions.0.question14days") : t("faq.questions.0.question"),
			answer: [
				{
					type: "text",
					content: isLegacy14DayTrialCohort ? t("faq.questions.0.answer14days") : t("faq.questions.0.answer"),
				},
			],
		},
		{
			question: t("faq.questions.1.question"),
			answer: [{ type: "text", content: t("faq.questions.1.answer") }],
		},
		{
			question: t("faq.questions.2.question"),
			answer: [
				{ type: "paragraph", content: t("faq.questions.2.answer.part1") },
				{ type: "paragraph", content: t("faq.questions.2.answer.part2") },
			],
		},
	];
}

const PricingContent = ({ isPublicPage = false, onClose, className }: IPricingContentProps) => {
	const { t, i18n } = useTranslation("translation", { keyPrefix: "pricingFlow.pricingPopup" });
	const { authStore, uiStore } = useAppStore();
	const { trackEvent } = usePostHog();
	const navigate = useSecureNavigate();
	const [selectedFeature, setSelectedFeature] = useState<string | null>(null);
	const [maxExpandedHeight, setMaxExpandedHeight] = useState<number>(0);
	const featureContentRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

	const { getFeatureContent, featureMinHeights, expandedFeatures, availableAPIs } = usePricingFeatureContent();

	const features: Features = t("pricingFeatures", {
		returnObjects: true,
	}) as Features;

	const { isUserCreatedBeforeGrowthDate } = useApiLimitStatus();

	const pricingCards = useMemo(() => {
		const cards = t("pricingCards", {
			returnObjects: true,
		}) as Record<string, any>;
		if (!isUserCreatedBeforeGrowthDate || !cards.freeTrial) {
			return cards;
		}
		const ft = cards.freeTrial;
		return {
			...cards,
			freeTrial: {
				title: ft.title,
				description: ft.description14days,
				subDescription: ft.subDescription14days,
				price: {
					title: ft.price.title14days,
					description: ft.price.description14days,
				},
				buttonText: ft.buttonText,
			},
		};
	}, [isUserCreatedBeforeGrowthDate, i18n.language, t]);

	const currentPlanName = authStore.orgSubscription?.plan_name.toLowerCase();

	const isCurrentPlan = (planKey: string): boolean => {
		if (planKey === "briaService" && currentPlanName === ORG_SUBSCRIPTION_PLANS.business.name) {
			return true;
		}
		if (planKey === "enterprise" && currentPlanName === ORG_SUBSCRIPTION_PLANS.custom.name) {
			return true;
		}
		if (planKey === "selfService" && currentPlanName === ORG_SUBSCRIPTION_PLANS.starter.name) {
			return true;
		}
		if (
			planKey === "freeTrial" &&
			isUserCreatedBeforeGrowthDate &&
			currentPlanName === ORG_SUBSCRIPTION_PLANS.free.name
		) {
			return true;
		}
		return false;
	};

	// Track pricing page viewed when component loads
	useEffect(() => {
		const source = isPublicPage ? "public_page" : "dashboard";

		trackEvent(POSTHOG_EVENTS.PRICING_PAGE_VIEWED, {
			source: source,
		});
	}, [isPublicPage, trackEvent]);

	// Calculate max height for expanded features
	useEffect(() => {
		if (selectedFeature) {
			setTimeout(() => {
				const heights: number[] = [];
				Object.keys(pricingCards).forEach((planKey) => {
					const content = getFeatureContent(planKey, selectedFeature);
					if (content.features && content.features.length > 0) {
						const ref = featureContentRefs.current[`${planKey}-${selectedFeature}`];
						if (ref) {
							// Get the actual content height without any padding/margins
							const computedStyle = window.getComputedStyle(ref);
							const contentHeight =
								ref.scrollHeight -
								parseInt(computedStyle.paddingTop) -
								parseInt(computedStyle.paddingBottom);
							heights.push(contentHeight);
						}
					}
				});

				if (heights.length > 0) {
					const maxHeight = Math.max(...heights);
					setMaxExpandedHeight(maxHeight);
				}
			}, 0);
		} else {
			setMaxExpandedHeight(0);
		}
	}, [selectedFeature, pricingCards]);

	const handleFeatureClick = (featureId: string | null) => {
		setSelectedFeature(featureId);

		// Track plan comparison viewed when feature is selected
		if (featureId) {
			const featureTitle = features[featureId]?.title || featureId;

			trackEvent(POSTHOG_EVENTS.PLAN_COMPARISON_VIEWED, {
				feature: featureTitle,
			});
		}
	};

	const trackContactSalesClick = (planInterest: string) => {
		trackEvent(POSTHOG_EVENTS.CONTACT_SALES_CLICKED, {
			plan_interest: planInterest,
		});
	};

	const handleButtonClick = (planKey: string) => {
		const currentPlanName = authStore.orgSubscription?.plan_name;

		switch (planKey) {
			case "freeTrial":
				// Free Trial logic based on current subscription
				if (
					currentPlanName === ORG_SUBSCRIPTION_PLANS.starter.name ||
					currentPlanName === ORG_SUBSCRIPTION_PLANS.custom.name
				) {
					// User has paid plan - no action for free trial
					return;
				} else if (isPublicPage || !authStore.user) {
					const newPlatformUrl = ENV_VARS.VITE_NEW_PLATFORM_URL ?? "";
					if (newPlatformUrl) {
						const returnUrl = encodeURIComponent(
							`${window.location.origin}/${RouterConstants.AUTH_KEYS.fullPath}`,
						);
						window.location.href = `${newPlatformUrl}/login?returnUrl=${returnUrl}`;
					}
				} else if (authStore.userOrganizations && authStore.userOrganizations.length > 0) {
					navigate(RouterConstants.AUTH_KEYS.fullPath);
					onClose?.();
				} else {
					uiStore.showDialog("CreateOrganizationDialog");
					onClose?.();
				}
				break;
			case "selfService":
				window.open(LinkConstants.PLATFORM.BILLING, "_blank", "noopener,noreferrer");
				onClose?.();
				break;
			case "enterprise":
			case "briaService":
				// Custom plan logic based on current subscription
				if (currentPlanName === ORG_SUBSCRIPTION_PLANS.custom.name) {
					// User has custom plan - show manage plan (contact us)
					trackContactSalesClick("custom");
					window.open("https://bria.ai/contact-us", "_blank");
				} else {
					// Default behavior for non-custom plans
					trackContactSalesClick("enterprise");
					window.open("https://bria.ai/contact-us", "_blank");
				}
				break;
			default:
				break;
		}
	};

	return (
		<Container maxWidth="xl" className={className}>
			<Box className={styles.pricingHeaderContainer}>
				<Typography className={styles.pricingHeader} variant="h2">
					{t("briaAi")} {t("title")}
				</Typography>
				<Typography className={styles.pricingDescription} variant="body1">
					{t("description")}
				</Typography>
			</Box>
			<Box className={styles.pricingContainer}>
				<Box className={styles.featuresContainer}>
					{Object.entries(features).map(([_key, feature]: [string, any]) => {
						const featureMinHeight = featureMinHeights[feature.id] || 48;
						const buttonContent = (
							<BriaButton
								className={clsx(styles.featureBtn, {
									[styles.selectedFeature]:
										expandedFeatures.includes(feature.id) &&
										selectedFeature &&
										availableAPIs.includes(selectedFeature),
									[styles.fixedButton]: !expandedFeatures.includes(feature.id),
									[styles.hidden]: feature.hidden,
									[styles.tooltip]: feature.tooltip,
								})}
								buttonType="textMedium"
								onClick={() => {
									if (!expandedFeatures.includes(feature.id)) {
										return;
									}
									const newFeatureId = selectedFeature === feature.id ? null : feature.id;
									handleFeatureClick(newFeatureId);
									setMaxExpandedHeight(0);
								}}
								style={{
									marginBottom:
										maxExpandedHeight > 0 && selectedFeature === feature.id
											? `${maxExpandedHeight - 12}px`
											: "0px",
									minHeight: `${featureMinHeight}px`,
								}}
							>
								{feature.label || feature.title}
							</BriaButton>
						);

						return feature.tooltip ? (
							<BriaTooltip
								key={feature.id}
								title={feature.tooltip}
								className={clsx({ [styles.tooltipWrapper]: !isPublicPage })}
								displayOn="hover"
								mode="dark"
							>
								{buttonContent}
							</BriaTooltip>
						) : (
							<Box key={feature.id}>{buttonContent}</Box>
						);
					})}
				</Box>
				{Object.entries(pricingCards).map(([key, card]: [string, any]) => (
					<PricingCard
						key={key}
						{...card}
						className={clsx(styles.pricingCard, {
							[styles.currentPlan]: isCurrentPlan(key),
						})}
						buttonType={isCurrentPlan(key) ? "primaryMedium" : "tertiaryMedium"}
						planKey={key}
						features={features}
						getFeatureContent={getFeatureContent}
						selectedFeature={selectedFeature}
						onFeatureClick={handleFeatureClick}
						maxExpandedHeight={maxExpandedHeight}
						featureContentRefs={featureContentRefs}
						featureMinHeights={featureMinHeights}
						onButtonClick={() => handleButtonClick(key)}
					/>
				))}
			</Box>

			<Box className={styles.faqContainer}>
				<BriaFaqSection
					title={t("faq.title")}
					faqItems={getPricingFaqs(t, isUserCreatedBeforeGrowthDate)}
					className={styles.faqSection}
					titleClassName={styles.faqTitle}
					accordionClassName={styles.faqAccordion}
				/>
			</Box>
		</Container>
	);
};

export default observer(PricingContent);
